import React, { useEffect, useState, useCallback } from "react";
import { getAllPackages, deletePackage } from "../lib/packages";
import { getAllCustomers } from "../lib/users";
import { getAllShipments, markShipmentDelivered, deleteShipment, updateShipment } from "../lib/shipments";
import { getAllClaims, deleteClaim } from "../lib/claims";
import { getAllRequests, deleteRequest, REQUEST_SERVICES } from "../lib/requests";
import { getAllInvoices, markInvoicePaid } from "../lib/invoices";
import { useAuth } from "../context/AuthContext";
import { StatCard, StatusPill, PkgThumb, EmptyState, Spinner, toast, formatDate } from "../components/UI";
import { detectCarrier, getTrackingURL } from "../lib/carrier";
import AddPackageModal from "../components/AddPackageModal";
import EditStatusModal from "../components/EditStatusModal";
import PackageDetailModal from "../components/PackageDetailModal";
import CreateCustomerModal from "../components/CreateCustomerModal";
import CreateShipmentModal from "../components/CreateShipmentModal";
import ReviewClaimModal from "../components/ReviewClaimModal";
import ReviewRequestModal from "../components/ReviewRequestModal";
import InvoiceModal from "../components/InvoiceModal";
import ShippingRateModal from "../components/ShippingRateModal";
import BalanceModal from "../components/BalanceModal";
import { getAllShopOrders, updateShopOrder, deleteShopOrder } from "../lib/shopOrders";
import { WAREHOUSE_LIST, UNITS } from "../lib/warehouses";

const CLAIM_STATUS_STYLE = {
  "Open":      { background: "#E6F1FB", color: "#185FA5" },
  "In Review": { background: "#FAEEDA", color: "#854F0B" },
  "Resolved":  { background: "#E1F5EE", color: "#0F6E56" },
  "Rejected":  { background: "#FCEBEB", color: "#A32D2D" },
};

const REQ_STATUS_STYLE = {
  "Pending":     { background: "#E6F1FB", color: "#185FA5" },
  "In Progress": { background: "#FAEEDA", color: "#854F0B" },
  "Completed":   { background: "#E1F5EE", color: "#0F6E56" },
  "Rejected":    { background: "#FCEBEB", color: "#A32D2D" },
};

const CLAIM_TYPE_LABELS = {
  not_received:  "Not received at warehouse",
  not_delivered: "Shipped but not delivered",
  damaged:       "Arrived damaged",
  other:         "Other issue",
};

function filterPkgs(pkgs, q, wh) {
  const lq = q.toLowerCase();
  return pkgs.filter(p => {
    const matchQ  = !q  || [p.id, p.customerEmail, p.customerName, p.description].some(v => (v||"").toLowerCase().includes(lq));
    const matchWH = !wh || p.warehouse === wh;
    return matchQ && matchWH;
  });
}

export default function AdminPage() {
  const { user } = useAuth();
  const [packages,         setPackages]         = useState([]);
  const [customers,        setCustomers]        = useState([]);
  const [shipments,        setShipments]        = useState([]);
  const [claims,           setClaims]           = useState([]);
  const [requests,         setRequests]         = useState([]);
  const [invoices,         setInvoices]         = useState([]);
  const [viewInvoice,      setViewInvoice]      = useState(null);
  const [shopOrders,       setShopOrders]       = useState([]);
  const [showBalance,      setShowBalance]      = useState(false);
  const [reviewShopOrder,  setReviewShopOrder]  = useState(null);
  const [shopStatus,       setShopStatus]       = useState("");
  const [shopAdminNote,    setShopAdminNote]    = useState("");
  const [savingShop,       setSavingShop]       = useState(false);
  const [loading,          setLoading]          = useState(true);
  const [tab,              setTab]              = useState("packages");
  const [search,           setSearch]           = useState("");
  const [whFilter,         setWhFilter]         = useState("");
  const [selected,         setSelected]         = useState([]);
  const [showAdd,          setShowAdd]          = useState(false);
  const [showCreateCust,   setShowCreateCust]   = useState(false);
  const [showShipment,     setShowShipment]     = useState(false);
  const [showRates,        setShowRates]        = useState(false);
  const [editPkg,          setEditPkg]          = useState(null);
  const [detailPkg,        setDetailPkg]        = useState(null);
  const [editShipment,     setEditShipment]     = useState(null);
  const [editShipTracking, setEditShipTracking] = useState("");
  const [editShipNotes,    setEditShipNotes]    = useState("");
  const [editShipSaving,   setEditShipSaving]   = useState(false);
  const [reviewClaim,      setReviewClaim]      = useState(null);
  const [reviewRequest,    setReviewRequest]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pkgs, custs, shps, clms, reqs, invs, shops] = await Promise.all([
        getAllPackages(),
        getAllCustomers(),
        getAllShipments(),
        getAllClaims().catch(() => []),
        getAllRequests().catch(() => []),
        getAllInvoices().catch(() => []),
        getAllShopOrders().catch(() => []),
      ]);
      setPackages(pkgs || []); setCustomers(custs || []); setShipments(shps || []); setClaims(clms || []); setRequests(reqs || []); setInvoices(invs || []); setShopOrders(shops || []);
    } catch (e) {
      toast("Failed to load: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete        = async (pkg) => { if (!window.confirm("Delete this package?")) return; try { await deletePackage(pkg.id); toast("Package deleted."); setSelected(s => s.filter(id => id !== pkg.id)); load(); } catch (e) { toast(e.message, "error"); } };
  const toggleSelect        = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const handleMarkDelivered = async (shp) => { if (!window.confirm("Mark entire shipment as delivered?")) return; try { await markShipmentDelivered(shp.id, shp.packageIds); toast("Marked delivered!"); load(); } catch (e) { toast(e.message, "error"); } };
  const handleDeleteShipment= async (shp) => { if (!window.confirm("Delete shipment? Packages revert to Received.")) return; try { await deleteShipment(shp.id, shp.packageIds); toast("Shipment deleted."); load(); } catch (e) { toast(e.message, "error"); } };
  const openEditShipment    = (shp) => { setEditShipment(shp); setEditShipTracking(shp.trackingNumber); setEditShipNotes(shp.notes || ""); };
  const saveEditShipment    = async () => { setEditShipSaving(true); try { await updateShipment(editShipment.id, editShipment.packageIds, editShipTracking, editShipNotes); toast("Updated!"); setEditShipment(null); load(); } catch (e) { toast(e.message, "error"); } finally { setEditShipSaving(false); } };
  const handleDeleteClaim   = async (id) => { if (!window.confirm("Delete claim?")) return; try { await deleteClaim(id); toast("Deleted."); load(); } catch (e) { toast(e.message, "error"); } };
  const handleDeleteRequest = async (id) => { if (!window.confirm("Delete request?")) return; try { await deleteRequest(id); toast("Deleted."); load(); } catch (e) { toast(e.message, "error"); } };

  const receivedPackages = (packages || []).filter(p => p.status === "Received");
  const visibleReceived  = filterPkgs(receivedPackages, search, whFilter);
  const selectedPackages = packages.filter(p => selected.includes(p.id));
  const openClaims       = (claims || []).filter(c => c.status === "Open" || c.status === "In Review");
  const openRequests     = (requests || []).filter(r => r.status === "Pending" || r.status === "In Progress");

  const TABS = [
    { key: "packages",  label: "Packages" },
    { key: "shipments", label: "Shipments", badge: (shipments || []).filter(s => s.status === "Shipped").length,  badgeStyle: { background: "var(--amber-light)", color: "var(--amber-dark)" } },
    { key: "requests",  label: "Requests",  badge: openRequests.length,  badgeStyle: { background: "var(--amber-light)", color: "var(--amber-dark)" } },
    { key: "claims",    label: "Claims",    badge: openClaims.length,    badgeStyle: { background: "#FCEBEB", color: "#A32D2D" } },
    { key: "invoices", label: "Invoices", badge: (invoices || []).filter(i => i.status === "Unpaid").length, badgeStyle: { background: "#FCEBEB", color: "#A32D2D" } },
    { key: "shop",      label: "Shop orders", badge: (shopOrders || []).filter(o => o.status === "Pending").length, badgeStyle: { background: "var(--amber-light)", color: "var(--amber-dark)" } },
    { key: "customers", label: "Customers" },
  ];

  return (
    <div className="page-wrap">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2>Warehouse Dashboard</h2>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 3 }}>USA Warehouse · {user?.name || user?.email}</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowRates(true)}>🚚 Shipping rates</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowBalance(true)}>💰 Balances</button>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard label="Total packages"  value={packages.length} />
        <StatCard label="In transit"      value={packages.filter(p => p.status === "Shipped").length}   color="#854F0B" />
        <StatCard label="Delivered"       value={packages.filter(p => p.status === "Delivered").length} color="#0F6E56" />
        <StatCard label="Open items"      value={openClaims.length + openRequests.length} color={openClaims.length + openRequests.length > 0 ? "#A32D2D" : undefined} />
      </div>

      <div className="tab-bar">
        {TABS.map(t => (
          <div key={t.key} className={`tab-item ${tab === t.key ? "active" : ""}`} onClick={() => { setTab(t.key); setSelected([]); }}>
            {t.label}
            {t.badge > 0 && <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, padding: "1px 6px", borderRadius: 20, ...t.badgeStyle }}>{t.badge}</span>}
          </div>
        ))}
      </div>

      {/* ── Packages ── */}
      {tab === "packages" && (
        <>
          <div className="search-bar">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by ID, customer, description..." />
<select value={whFilter} onChange={e => setWhFilter(e.target.value)} style={{ width: "auto" }}>
              <option value="">All warehouses</option>
              {WAREHOUSE_LIST.map(w => <option key={w.id} value={w.id}>{w.flag} {w.label}</option>)}
            </select>
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add package</button>
          </div>
          {selected.length > 0 && (
            <div className="selection-bar">
              <span>{selected.length} package{selected.length !== 1 ? "s" : ""} selected</span>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={() => setShowShipment(true)}>Ship together</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setSelected([])}>Clear</button>
              </div>
            </div>
          )}
          {loading ? <Spinner msg="Loading packages..." /> : (
            visibleReceived.length === 0
              ? <EmptyState msg="No received packages. Shipped packages are in the Shipments tab." />
              : visibleReceived.map(pkg => (
                <div className={"pkg-card" + (selected.includes(pkg.id) ? " pkg-card-selected" : "")} key={pkg.id}>
                  <div className={"pkg-checkbox" + (selected.includes(pkg.id) ? " checked" : "")} onClick={() => toggleSelect(pkg.id)}>{selected.includes(pkg.id) && <span>✓</span>}</div>
                  <PkgThumb photoURL={pkg.photoURL} size={80} />
                  <div className="pkg-info">
                  <div className="pkg-id">{pkg.trackingNumber || pkg.id}</div>
                    <div className="pkg-name">{pkg.description || "Package"}</div>
                    <div className="pkg-meta">
                      <StatusPill status={pkg.status} />
                      <span className="pkg-detail">👤 {pkg.customerName} · {pkg.customerEmail}</span>
                      <span className="pkg-detail">⚖ {pkg.weight} lbs{pkg.dimensions ? ` · 📐 ${pkg.dimensions}` : ""}</span>
                      {pkg.eta && <span className="pkg-detail">📅 ETA: {formatDate(pkg.eta)}</span>}
                      {pkg.warehouse && <span className="pkg-detail">{WAREHOUSE_LIST.find(w=>w.id===pkg.warehouse)?.flag} {pkg.warehouse}</span>}
                      {pkg.unit && <span style={{ fontSize:11, background:"var(--bg-tertiary)", padding:"1px 8px", borderRadius:10, fontWeight:600 }}>{UNITS[pkg.unit]?.label||pkg.unit}</span>}
                    </div>
                    <div className="pkg-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => setDetailPkg(pkg)}>View</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditPkg(pkg)}>Update status</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(pkg)}>Delete</button>
                    </div>
                  </div>
                </div>
              ))
          )}
        </>
      )}

      {/* ── Shipments ── */}
      {tab === "shipments" && (
        shipments.length === 0 ? <EmptyState msg="No shipments yet." /> :
        (shipments || []).map(shp => {
          const pkgsIn      = packages.filter(p => shp.packageIds?.includes(p.id));
          const carrierInfo = detectCarrier(shp.trackingNumber || "");
          const trackingURL = carrierInfo ? getTrackingURL(shp.trackingNumber, carrierInfo.code) : null;
          return (
            <div className="card" key={shp.id} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "monospace", marginBottom: 2 }}>{shp.id}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "monospace" }}>{shp.trackingNumber}</div>
                    {carrierInfo && <span className="carrier-badge" style={{ background: carrierInfo.bg, color: carrierInfo.color, padding: "3px 10px", fontSize: 12, margin: 0 }}><span className="carrier-dot" style={{ background: carrierInfo.color }} />{carrierInfo.name}</span>}
                    {trackingURL && <a href={trackingURL} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ fontSize: 12, textDecoration: "none" }}>Track on {carrierInfo.name} ↗</a>}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>{shp.packageIds?.length} packages · {formatDate(shp.createdAt)}</div>
                  {shp.notes && <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>{shp.notes}</div>}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <StatusPill status={shp.status} />
                  <button className="btn btn-ghost btn-sm" onClick={() => openEditShipment(shp)}>Edit</button>
                  {shp.status === "Shipped" && <button className="btn btn-primary btn-sm" onClick={() => handleMarkDelivered(shp)}>Mark delivered</button>}
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteShipment(shp)}>Delete</button>
                </div>
              </div>
              <div style={{ borderTop: "0.5px solid var(--border)", paddingTop: 10 }}>
                {pkgsIn.map(p => (
                  <div key={p.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "6px 0", borderBottom: "0.5px solid var(--border)" }}>
                    <PkgThumb photoURL={p.photoURL} size={40} />
                    <div style={{ flex: 1 }}><div style={{ fontSize: 12, fontFamily: "monospace", color: "var(--text-muted)" }}>{p.id}</div><div style={{ fontSize: 14, fontWeight: 600 }}>{p.description || "Package"}</div></div>
                    <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{p.customerName}</div>
                    <StatusPill status={p.status} />
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}

      {/* ── Requests ── */}
      {tab === "requests" && (
        (requests || []).length === 0 ? <EmptyState msg="No special requests yet." /> :
        (requests || []).map(r => {
          const style     = REQ_STATUS_STYLE[r.status] || REQ_STATUS_STYLE["Pending"];
          const svcLabels = r.services?.map(id => { const s = REQUEST_SERVICES.find(x => x.id === id); return s ? `${s.icon} ${s.label}` : id; }) || [];
          return (
            <div className="card" key={r.id} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{r.customerName} <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 13 }}>· {r.customerEmail}</span></div>
                  {r.packageId && <div style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text-faint)", marginTop: 2 }}>Package: {r.packageId} · {r.packageDesc}</div>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                  <span className="status-pill" style={style}>{r.status}</span>
                  {r.totalCost > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: "var(--green-dark)" }}>${r.totalCost}</span>}
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 8 }}>
                {svcLabels.map((s, i) => <span key={i} style={{ background: "var(--green-light)", color: "var(--green-dark)", fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 20 }}>{s}</span>)}
              </div>
              {r.note && <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>{r.note}</p>}
              {r.adminNotes && <div style={{ background: "var(--green-light)", borderRadius: "var(--radius)", padding: "8px 12px", fontSize: 13, marginBottom: 8 }}><span style={{ fontWeight: 600, color: "var(--green-dark)" }}>Response: </span>{r.adminNotes}</div>}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "var(--text-faint)", flex: 1 }}>{formatDate(r.createdAt)}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => setReviewRequest(r)}>Review</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDeleteRequest(r.id)}>Delete</button>
              </div>
            </div>
          );
        })
      )}

      {/* ── Claims ── */}
      {tab === "claims" && (
        (claims || []).length === 0 ? <EmptyState msg="No claims yet." /> :
        (claims || []).map(c => {
          const style = CLAIM_STATUS_STYLE[c.status] || CLAIM_STATUS_STYLE["Open"];
          return (
            <div className="card" key={c.id} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{c.customerName} <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 13 }}>· {c.customerEmail}</span></div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)" }}>{CLAIM_TYPE_LABELS[c.type] || c.type}</div>
                  {c.packageId && <div style={{ fontSize: 11, fontFamily: "monospace", color: "var(--text-faint)" }}>Package: {c.packageId}</div>}
                </div>
                <span className="status-pill" style={style}>{c.status}</span>
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 8 }}>{c.description}</p>
              {c.adminNotes && <div style={{ background: "var(--green-light)", borderRadius: "var(--radius)", padding: "8px 12px", fontSize: 13, marginBottom: 8 }}><span style={{ fontWeight: 600, color: "var(--green-dark)" }}>Response: </span>{c.adminNotes}</div>}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: "var(--text-faint)", flex: 1 }}>{formatDate(c.createdAt)}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => setReviewClaim(c)}>Review</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDeleteClaim(c.id)}>Delete</button>
              </div>
            </div>
          );
        })
      )}


      {/* ── Invoices ── */}
      {tab === "invoices" && (
        (invoices || []).length === 0 ? <EmptyState msg="No invoices yet. Invoices are generated automatically when you create a shipment." /> :
        <div>
          {(invoices || []).map(inv => {
            return (
              <div className="card" key={inv.id} style={{ marginBottom: 10, cursor: "pointer" }} onClick={() => setViewInvoice(inv)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 12, fontFamily: "monospace", color: "var(--text-muted)", marginBottom: 2 }}>{inv.invoiceNumber}</div>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{inv.customerName} — ${inv.totalDue}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{inv.packageIds?.length} pkg · {formatDate(inv.createdAt)}</div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: inv.status === "Paid" ? "var(--green-light)" : "#FAEEDA", color: inv.status === "Paid" ? "var(--green-dark)" : "#854F0B" }}>
                      {inv.status === "Paid" ? "✓ Paid" : "⏳ Unpaid"}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: parseFloat(inv.profit) >= 0 ? "var(--green-dark)" : "var(--red-dark)" }}>
                      Profit: ${inv.profit}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}


      {/* ── Shop orders ── */}
      {tab === "shop" && (
        (shopOrders || []).length === 0 ? <EmptyState msg="No shop orders yet." /> :
        (shopOrders || []).map(o => {
          const statusStyle = { "Pending": { background: "#E6F1FB", color: "#185FA5" }, "Confirmed": { background: "#FAEEDA", color: "#854F0B" }, "Ordered": { background: "#FAEEDA", color: "#854F0B" }, "Arrived": { background: "#E1F5EE", color: "#0F6E56" }, "Rejected": { background: "#FCEBEB", color: "#A32D2D" } };
          const st = statusStyle[o.status] || statusStyle["Pending"];
          return (
            <div className="card" key={o.id} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{o.customerName} <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 13 }}>· {o.customerEmail}</span></div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{o.items?.length} items · ${o.grandTotal} · {formatDate(o.createdAt)}</div>
                </div>
                <span className="status-pill" style={st}>{o.status}</span>
              </div>
              {o.items?.map((item, i) => (
                <div key={i} style={{ padding: "6px 0", borderTop: "0.5px solid var(--border)", fontSize: 13 }}>
                  <a href={item.link} target="_blank" rel="noreferrer" style={{ color: "var(--blue)", wordBreak: "break-all" }}>{item.link}</a>
                  <div style={{ color: "var(--text-muted)", marginTop: 2 }}>×{item.quantity} · Price: ${item.price} · Discount: {item.discount || "—"} · Total: ${item.total}{item.notes ? ` · ${item.notes}` : ""}</div>
                </div>
              ))}
              {o.adminNotes && <div style={{ background: "var(--green-light)", borderRadius: "var(--radius)", padding: "8px 12px", fontSize: 13, marginTop: 10 }}><span style={{ fontWeight: 600, color: "var(--green-dark)" }}>Your response: </span>{o.adminNotes}</div>}
              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {["Confirmed","Ordered","Arrived","Rejected"].map(s => (
                  <button key={s} className={`btn btn-sm ${o.status === s ? "btn-primary" : "btn-ghost"}`} onClick={async () => { await updateShopOrder(o.id, s, o.adminNotes); toast("Updated!"); load(); }}>{s}</button>
                ))}
                <button className="btn btn-danger btn-sm" onClick={async () => { if (window.confirm("Delete?")) { await deleteShopOrder(o.id); toast("Deleted."); load(); } }}>Delete</button>
              </div>
            </div>
          );
        })
      )}

      {/* ── Customers ── */}
      {tab === "customers" && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button className="btn btn-primary" onClick={() => setShowCreateCust(true)}>+ Create customer account</button>
          </div>
          <div className="card">
            {(customers || []).length === 0 ? <EmptyState msg="No customers yet." /> : (customers || []).map(c => {
              const count    = packages.filter(p => p.customerEmail === c.email).length;
              const initials = (c.name || c.email).split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
              return (
                <div className="customer-row" key={c.uid}>
                  <div className="avatar">{initials}</div>
                  <div className="customer-info"><div className="customer-name">{c.name||"—"}</div><div className="customer-email">{c.email}</div></div>
                  <span className="status-pill pill-blue">{count} pkg{count!==1?"s":""}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Edit shipment modal */}
      {editShipment && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setEditShipment(null)}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-head"><span className="modal-title">Edit shipment</span><button className="close-btn" onClick={() => setEditShipment(null)}>✕</button></div>
            <div className="form-row"><label>Tracking number</label><input value={editShipTracking} onChange={e => setEditShipTracking(e.target.value)} style={{ fontFamily: "monospace" }} /></div>
            <div className="form-row"><label>Notes</label><textarea rows={2} value={editShipNotes} onChange={e => setEditShipNotes(e.target.value)} /></div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditShipment(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEditShipment} disabled={editShipSaving}>{editShipSaving ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>
      )}

      {showAdd        && <AddPackageModal      onClose={() => setShowAdd(false)}        onSaved={load} />}
      {showCreateCust && <CreateCustomerModal  onClose={() => setShowCreateCust(false)} onSaved={load} />}
      {showShipment   && <CreateShipmentModal  packages={selectedPackages} onClose={() => setShowShipment(false)} onSaved={() => { setSelected([]); load(); }} />}
      {showRates      && <ShippingRateModal    onClose={() => setShowRates(false)} />}
      {showBalance    && <BalanceModal          onClose={() => setShowBalance(false)} />}
      {editPkg        && <EditStatusModal      pkg={editPkg}      onClose={() => setEditPkg(null)}      onSaved={load} />}
      {detailPkg      && <PackageDetailModal   pkg={detailPkg}    onClose={() => setDetailPkg(null)}    isAdmin />}
      {reviewClaim    && <ReviewClaimModal     claim={reviewClaim}   onClose={() => setReviewClaim(null)}   onSaved={load} />}
      {reviewRequest  && <ReviewRequestModal   request={reviewRequest} onClose={() => setReviewRequest(null)} onSaved={load} />}
            {viewInvoice   && <InvoiceModal         invoice={viewInvoice} packages={packages.filter(p => viewInvoice.packageIds?.includes(p.id))} requests={[]} isAdmin onClose={() => setViewInvoice(null)} onPaid={load} />}
    </div>
  );
}
